import {computed, makeObservable, observable} from 'mobx';

import {BaseModuleState} from "../../../../../infrastructure/models/base-module-state";
import {BaseMobxState} from "../../../../../infrastructure/mobx/base-mobx-state";
import {AppStore} from "../../../../../core/app/app-store";
import {LoggerUtils} from "../../../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../../../core/models/device.view-model";
import {TermsUtils} from "../../../../../infrastructure/utils/terms-utils";
import {NavigationManager} from "../../../../../core/managers/navigation-manager";
import {SideBarModuleConfig} from "../side-bar-module.config";
import {DASHBOARD} from "../../../../../containers/dashboard-container/dashboard-container.consts";
import {PAGES, ROUTE_NAMES} from "../../../../../core/consts";
import {SideBarEntityScoresTabModel} from "./side-bar.entity-scores.tab-model";
import {SIDE_BAR} from "../side-bar-module.consts";
import {WebsiteModuleState} from "../../../models/website-module.state";
import {ScoresBarWidgetConfig} from "../../../../../widgets/scores-bar-widget/scores-bar-widget.config";
import {EXTERNAL_AD_CONFIG} from "../../../consts/external-ad-config";
import {parseISO} from "date-fns";
import {
  GameCenterLinkParams
} from "../../../../../containers/game-center-container/models/game-center-link-params";

export class SideBarModuleState extends BaseModuleState {
  @observable selectedTabIndex = 0;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, SideBarModuleConfig, WebsiteModuleState];

  constructor(
      navigationManager: NavigationManager,
      appStore: AppStore,
      logger: LoggerUtils,
      datetimeUtils: DatetimeUtils,
      device: DeviceViewModel,
      termsUtils: TermsUtils,
      config: ScoresBarWidgetConfig,
      websiteModuleState: WebsiteModuleState,
  ) {
    super(navigationManager, appStore, logger, datetimeUtils, device, termsUtils, config);

    this.websiteModuleState = websiteModuleState;

    makeObservable(this)
  }

  @computed get sportTypeId(): Number {
    return this.appState.context.sportType?.id;
  }

  @computed get showOdds(): Boolean {
    return this.appState.showOdds;
  }

  @computed get myScoresPage(): Boolean {
    return this.appState.myScoresPage;
  }

  @computed get isToday(): Boolean {
    return this.currentLinkModel?.params?.filter === undefined
  }

  @computed get isYesterdayFilter(): Boolean {
    return this.currentLinkModel.params.filter === DASHBOARD.FILTERS.yesterday
  }

  @computed get isTomorrowFilter(): Boolean {
    return this.currentLinkModel.params.filter === DASHBOARD.FILTERS.tomorrow
  }

  @computed get isBeforeYesterdayFilter(): Boolean {
    return this.currentLinkModel.params.filter === DASHBOARD.FILTERS.results && this.currentLinkModel.hash === '';
  }

  @computed get isAfterTomorrowFilter(): Boolean {
    return this.currentLinkModel.params.filter === DASHBOARD.FILTERS.fixtures && this.currentLinkModel.hash === '';
  }

  @computed get date(): Date {
    let dateFromUrl: Date = null;

    if(this.isYesterdayFilter) {
      const yesterday: Date = this.datetimeUtils.getRelativeDate(-1);
      dateFromUrl = yesterday.midnight()
    } else if(this.isTomorrowFilter){
      const tomorrow: Date = this.datetimeUtils.getRelativeDate(1);
      dateFromUrl = tomorrow.midnight()
    } else if(this.isBeforeYesterdayFilter) {
      const beforeYesterday: Date = this.datetimeUtils.getRelativeDate(-2);
      dateFromUrl = beforeYesterday.midnight()
    } else if(this.isAfterTomorrowFilter){
      const afterTomorrow: Date = this.datetimeUtils.getRelativeDate(2);
      dateFromUrl = afterTomorrow.midnight()
    } else if(this.currentLinkModel?.hashParams?.date && !this.isToday && !this.isLive) {
      dateFromUrl = parseISO(this.currentLinkModel.hashParams.date)
    }

    const todayDate: Date = (new Date()).midnight();

    const date: Date = dateFromUrl || todayDate;

    return date;
  }

  @computed get isLive(): Boolean {
    return this.currentLinkModel.params.filter === DASHBOARD.FILTERS.live
  }

  @computed get entityScoresTabs(): Array<SideBarEntityScoresTabModel> {
    return Object.values(SIDE_BAR.TABS).map((tab: SideBarEntityScoresTabModel) => new SideBarEntityScoresTabModel(tab, this.terms));
  }

  @computed get selectedFilterName(): Array<SideBarEntityScoresTabModel> {
    return this.entityScoresTabs.find((tab: SideBarEntityScoresTabModel) => tab.index === this.selectedTabIndex)?.filterName
  }

  @computed get isAllScores(): Boolean {
    return this.currentLinkModel.name === ROUTE_NAMES.allScores
  }

  @computed get isBettingPage(): Boolean {
    const bettingPages: Array<ROUTE_NAMES> = [ROUTE_NAMES.betting, ...(Object.values(PAGES.BETTING))];

    return bettingPages.includes(this.currentLinkModel?.name);
  }

  @computed get isMyScores(): Boolean {
    return this.currentLinkModel.name === ROUTE_NAMES.myScoresPage
  }

  @computed get isGameCenterPage(): Boolean {
    return [ROUTE_NAMES.gameCenterPage, Object.values(PAGES.GAME_CENTER)].flat()?.includes((this.currentLinkModel?.name))
  }

  @computed get isCompetitionPage(): Boolean {
    return [ROUTE_NAMES.competition, Object.values(PAGES.COMPETITION)].flat()?.includes((this.currentLinkModel?.name))
  }

  @computed get isCompetitorPage(): Boolean {
    return [ROUTE_NAMES.competitor, Object.values(PAGES.COMPETITOR)].flat()?.includes((this.currentLinkModel?.name))
  }

  @computed get isEntityScores(): Boolean {
    return this.isCompetitionScores || this.isCompetitorScores;
  }

  @computed get isGameCenter(): Boolean {
    return this.currentLinkModel.name === ROUTE_NAMES.game || this.isGameCenterPage;
  }

  @computed get isAthletes(): Boolean {
    return this.currentLinkModel.name === ROUTE_NAMES.athlete
  }

  @computed get isCompetitionScores(): Boolean {
    return this.currentLinkModel.name === ROUTE_NAMES.competitionScores || this.isCompetitionPage || this.isGameCenter;
  }

  @computed get isCompetitorScores(): Boolean {
    return this.currentLinkModel.name === ROUTE_NAMES.competitorScores || this.isCompetitorPage;
  }

  @computed get shouldRenderAllScoresSideBar(): Boolean {
    return this.isAllScores || this.isBettingPage;
  }

  @computed get entityId(): Number {
    const gameCenterParams = new GameCenterLinkParams(this.currentLinkModel?.params?.competitionKey, this.currentLinkModel?.params?.matchDetails, this.currentLinkModel?.params?.sportName);

    return this.isGameCenter ? gameCenterParams.competitionId : Number(this.currentLinkModel?.params.id)
  }

  get isPopulated(): Boolean {
    return true;
  }

  @computed get relatedEntitiesProps() {
    let relatedEntitiesProps = {};
    if (this.isCompetitionScores)
      relatedEntitiesProps = {competitionId: this.websiteModuleState.entitiesContext?.competition?.id};
    else if(this.isCompetitorScores){
      relatedEntitiesProps = {competitorId: this.websiteModuleState.entitiesContext?.competitor?.id};
    }
    else if(this.isAthletes){
      relatedEntitiesProps = {athleteId: this.websiteModuleState.entitiesContext?.athlete?.id};
    }

    return relatedEntitiesProps;
  }

  @computed get shouldRenderRelatedEntities(): boolean {
    return this.relatedEntitiesProps.competitionId || this.relatedEntitiesProps.competitorId || this.relatedEntitiesProps.athleteId;
  }

  @computed get shouldRenderExternalAd(): Boolean {
    return this.appState?.externalAdsModel?.shouldRender  && this.isAthletes;
  }

  @computed get id(): String {
    let id = this.config.name;
    if(this.currentLinkModel?.name) {
      id += `_${this.currentLinkModel?.name}`;
    }

    return id;
  }

  @computed get adConfig() {
    let adConfig = EXTERNAL_AD_CONFIG.DESKTOP.HP.AD336X280;
    if(this.isAthletes) {
      adConfig = EXTERNAL_AD_CONFIG.DESKTOP.ATHLETE.AD336X280;
    }
    else if(this.isCompetitionScores) {
      adConfig = EXTERNAL_AD_CONFIG.DESKTOP.COMPETITION.AD336X280;
    }
    else if(this.isCompetitorScores) {
      adConfig = EXTERNAL_AD_CONFIG.DESKTOP.COMPETITOR.AD336X280;
    }
    else if(this.isGameCenter) {
      adConfig = EXTERNAL_AD_CONFIG.DESKTOP.MATCH.AD336X280;
    }

    return adConfig;
  }
}